import { create } from 'zustand';
import { insightsService, downloadCsv } from '../services/insights.service';
import type {
  InsightsOverview,
  InsightsLead,
  InsightsLeadEvent,
  InsightsLeadFilterStatus,
} from '../types';

export type RangePreset = '7d' | '30d' | '90d' | 'custom';

const PRESET_DAYS: Record<Exclude<RangePreset, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const toDateInput = (date: Date): string => date.toISOString().slice(0, 10);

const rangeForPreset = (preset: Exclude<RangePreset, 'custom'>) => {
  const end = new Date();
  const start = new Date(end.getTime() - (PRESET_DAYS[preset] - 1) * 24 * 60 * 60 * 1000);
  return { startDate: toDateInput(start), endDate: toDateInput(end) };
};

interface InsightsState {
  // Range
  preset: RangePreset;
  startDate: string;
  endDate: string;

  // Overview
  overview: InsightsOverview | null;
  isLoadingOverview: boolean;

  // Leads
  leads: InsightsLead[];
  totalLeads: number;
  page: number;
  pageSize: number;
  statusFilter: InsightsLeadFilterStatus;
  search: string;
  isLoadingLeads: boolean;

  // Lead detail
  selectedLead: InsightsLead | null;
  timeline: InsightsLeadEvent[];
  isLoadingTimeline: boolean;

  isExporting: boolean;
  error: string | null;

  fetchOverview: () => Promise<void>;
  fetchLeads: (page?: number) => Promise<void>;
  refresh: () => Promise<void>;
  setPreset: (preset: RangePreset) => void;
  setCustomRange: (startDate: string, endDate: string) => void;
  setStatusFilter: (status: InsightsLeadFilterStatus) => void;
  setSearch: (search: string) => void;
  selectLead: (lead: InsightsLead | null) => Promise<void>;
  exportCsv: (type: 'leads' | 'metrics') => Promise<void>;
  clearError: () => void;
}

const initialRange = rangeForPreset('30d');

export const useInsightsStore = create<InsightsState>((set, get) => ({
  preset: '30d',
  startDate: initialRange.startDate,
  endDate: initialRange.endDate,

  overview: null,
  isLoadingOverview: false,

  leads: [],
  totalLeads: 0,
  page: 1,
  pageSize: 20,
  statusFilter: 'all',
  search: '',
  isLoadingLeads: false,

  selectedLead: null,
  timeline: [],
  isLoadingTimeline: false,

  isExporting: false,
  error: null,

  fetchOverview: async () => {
    const { startDate, endDate } = get();
    set({ isLoadingOverview: true, error: null });
    try {
      const overview = await insightsService.getOverview({ startDate, endDate });
      set({ overview, isLoadingOverview: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load insights',
        isLoadingOverview: false,
      });
    }
  },

  fetchLeads: async (page?: number) => {
    const { startDate, endDate, statusFilter, search, pageSize, page: currentPage } = get();
    const pageToFetch = page ?? currentPage;

    set({ isLoadingLeads: true, error: null });
    try {
      const response = await insightsService.getLeads({
        startDate,
        endDate,
        status: statusFilter,
        search: search || undefined,
        page: pageToFetch,
        pageSize,
      });
      set({
        leads: response.data,
        totalLeads: response.total,
        page: response.page,
        isLoadingLeads: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load leads',
        isLoadingLeads: false,
      });
    }
  },

  refresh: async () => {
    await Promise.all([get().fetchOverview(), get().fetchLeads(1)]);
  },

  setPreset: (preset) => {
    if (preset === 'custom') {
      set({ preset });
      return;
    }
    const range = rangeForPreset(preset);
    set({ preset, ...range, page: 1 });
    void get().refresh();
  },

  setCustomRange: (startDate, endDate) => {
    if (!startDate || !endDate || startDate > endDate) return;
    set({ preset: 'custom', startDate, endDate, page: 1 });
    void get().refresh();
  },

  setStatusFilter: (statusFilter) => {
    set({ statusFilter, page: 1 });
    void get().fetchLeads(1);
  },

  setSearch: (search) => {
    set({ search });
  },

  selectLead: async (lead) => {
    set({ selectedLead: lead, timeline: [] });
    if (!lead) return;

    set({ isLoadingTimeline: true });
    try {
      const timeline = await insightsService.getLeadTimeline(lead.id);
      set({ timeline, isLoadingTimeline: false });
    } catch {
      // A missing timeline shouldn't block the contact details in the drawer.
      set({ timeline: [], isLoadingTimeline: false });
    }
  },

  exportCsv: async (type) => {
    const { startDate, endDate, statusFilter, search } = get();
    set({ isExporting: true, error: null });
    try {
      const { csv, filename } = await insightsService.exportCsv(type, {
        startDate,
        endDate,
        status: statusFilter,
        search: search || undefined,
      });
      downloadCsv(csv, filename);
      set({ isExporting: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Export failed',
        isExporting: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));

export default useInsightsStore;
